import { ref, watch, computed, getCurrentInstance } from "@vue/composition-api";
// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import api from "@/services/api";
import { decrypt } from "@/utils/crypto";

export default function useCustomerComplainList() {
    const internalInstance = getCurrentInstance();
    const showCustomLoader = () => {
        return internalInstance?.proxy?.$showCustomLoader();
    };
    // Use toast
    const toast = useToast();
    const refComplainListTable = ref(null);
    const complainList = ref([]);

    // Table Handlers
    const tableColumns = [
        { key: "sl", sortable: false, label: "SL" },
        { key: "ticket_number", sortable: true, label: "Complain No." },
        { key: "service_type", sortable: true, label: "Service Type" },
        { key: "level", sortable: true, label: "Complexity" },
        { key: "created", sortable: true, label: "Created Date" },
        { key: "deadline", sortable: true, label: "Deadline" },
        { key: "closing_date", sortable: true, label: "Closing Date" },
    ];
    const perPage = ref(25);
    const totalComplains = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [25, 50, 100, 300, 500, 1000];
    const searchQuery = ref("");
    const sortBy = ref("effective_date");
    const isSortDirDesc = ref(true);
    const filters = ref({});

    const dataMeta = computed(() => {
        const localItemsCount = refComplainListTable.value
            ? refComplainListTable.value.localItems.length
            : 0;
        return {
            from:
                perPage.value * (currentPage.value - 1) +
                (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalComplains.value,
        };
    });

    const refetchData = () => {
        loadComplains();
    };

    const setFilter = (newFilters) => {
        filters.value = newFilters;
    };

    // Optimize the watch effect to prevent unnecessary API calls
    watch(
        [currentPage, perPage, searchQuery, filters],
        () => {
            loadComplains();
        },
        { deep: true },
    );

    const loadComplains = (ctx, callback) => {
        const userData = decrypt(localStorage.getItem("userData"));
        const username = userData ? userData.username : null;
        if (!username) {
            callback([]);
            return;
        }
        (async () => {
            const loader = showCustomLoader();

            try {
                const response = await api.get(
                    `/api/v1/helpdesk/ticket/list/`,
                    {
                        params: {
                            limit: perPage.value,
                            offset: (currentPage.value - 1) * perPage.value,
                            currentPage: currentPage.value,
                        },
                    },
                );

                loader?.hide();

                if (response.data) {
                    complainList.value = response.data || [];
                    totalComplains.value = response.data.length || 0;
                }
            } catch (error) {
                loader?.hide();
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Error fetching customer data",
                        text:
                            error.response?.data?.message ||
                            error.message ||
                            "Unknown error",
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });
                complainList.value = [];
                totalComplains.value = 0;
            }
        })();
    };

    const sortedComplains = computed(() => {
        const key = sortBy.value;
        const desc = isSortDirDesc.value;

        return [...complainList.value].sort((a, b) => {
            if (!a[key] || !b[key]) return 0;

            const aVal = a[key].toString().toLowerCase();
            const bVal = b[key].toString().toLowerCase();

            const result = aVal.localeCompare(bVal);
            return desc ? -result : result;
        });
    });

    // *===============================================---*
    // *--------- UI ---------------------------------------*
    // *===============================================---*

    const resolveEmployeeStatusVariant = (is_done) => {
        if (is_done == "0") return "danger";
        if (is_done == "1") return "success";
        return "primary";
    };

    return {
        sortedComplains,
        complainList,
        loadComplains,
        tableColumns,
        perPage,
        currentPage,
        totalComplains,
        dataMeta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        refComplainListTable,

        resolveEmployeeStatusVariant,
        refetchData,

        // Extra Filters
        setFilter,
    };
}
